import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CircleAlert, UserPlus } from 'lucide-react';
import { getCourseEnrollments, grantCourseAccess } from '../../api/enrollments';
import { getCourseTariffs } from '../../api/tariffs';
import { getCohorts } from '../../api/cohorts';
import { unwrapList, unwrapMeta } from '../../utils/api';
import { formatDate, formatMoney, formatPercent, formatPhone } from '../../utils/datetime';
import { usePagination } from '../../hooks/usePagination';
import { useScrollFade } from '../../hooks/useScrollFade';
import { useCourseTitle } from '../../hooks/useCourseTitle';
import Avatar from '../../components/Avatar/Avatar';
import Dropdown from '../../components/Dropdown/Dropdown';
import Modal from '../../components/Modal/Modal';
import PageHead from '../../components/PageHead/PageHead';
import Pagination from '../../components/Pagination/Pagination';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import SearchInput from '../../components/SearchInput/SearchInput';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import TableCard from '../../components/TableCard/TableCard';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import { useI18n } from '../../i18n/I18nContext';
import './CourseEnrollments.css';

/**
 * B2B license cell (source, org, expiry) — rendered only from fields the API actually returns.
 * Older/plain enrollment rows without these fields (source/org/expiresAt all absent) render
 * nothing extra here. Two lines at most: the pills, then the organisation's name.
 */
function EnrollmentLicense({ enrollment, t }) {
  const source = enrollment?.source;
  const orgName = enrollment?.org?.name || enrollment?.organization?.name;
  const expiresAt = enrollment?.expiresAt;
  const isExpired = Boolean(expiresAt) && new Date(expiresAt) < new Date();

  if (!source && !orgName && !expiresAt) return null;

  return (
    <div className="enr-license">
      {(source || expiresAt) && (
        <div className="enr-badges">
          {source === 'LICENSE' && (
            <span className="ds-badge ds-badge-purple">{t('activities.enrollmentBadges.sourceLicense')}</span>
          )}
          {source && source !== 'LICENSE' && (
            <span className="ds-badge ds-badge-success">{t('activities.enrollmentBadges.sourcePaid')}</span>
          )}
          {expiresAt && (
            <span className={`ds-badge ${isExpired ? 'ds-badge-danger' : 'ds-badge-warning'}`}>
              {isExpired
                ? t('activities.enrollmentBadges.expired')
                : t('activities.enrollmentBadges.expiresOn', { date: formatDate(expiresAt) })}
            </span>
          )}
        </div>
      )}
      {orgName && <span className="enr-org" title={orgName}>{orgName}</span>}
    </div>
  );
}

// What the status pill and the badge tone show. Mirrors the backend's `status` filter
// (cancelled / completed / active) so a row always sits under the status it is filtered by.
const getEnrollmentStatus = (enrollment) => {
  if (enrollment.status) return enrollment.status;
  // Access comes first, as in the backend's `status` filter: a refunded, revoked or expired
  // enrollment is cancelled even when the student had finished the course.
  if (enrollment.isActive === false) return 'cancelled';
  if (enrollment.completedAt) return 'completed';
  return 'active';
};

const STATUS_FILTERS = ['', 'active', 'completed', 'cancelled'];

const SEARCH_DEBOUNCE_MS = 300;

// More rows than this and the table caps its height, so its head stays in view while it scrolls.
const TALL_ROWS = 12;

const EMPTY_GRANT = { phone: '', firstName: '', lastName: '', pricingTierId: '', note: '' };

function CourseEnrollments({ courseId: courseIdProp, embedded: embeddedProp } = {}) {
  const { t } = useI18n();
  const params = useParams();
  const courseId = courseIdProp || params.courseId || params.id;
  // The course workspace hands its course id down; the standalone route reads it from the URL
  // and carries its own page header.
  const embedded = embeddedProp ?? Boolean(courseIdProp);
  // Only the standalone header names the course: inside the workspace the hero above does.
  const courseTitle = useCourseTitle(embedded ? null : courseId);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);
  const [search, setSearch] = useState('');
  // What the roster is loaded with: `search` a beat after the last keystroke.
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_GRANT);
  const [saving, setSaving] = useState(false);
  const [grantError, setGrantError] = useState(null);
  // Set by a submit that was missing a field: only then do the empty fields show as invalid.
  const [grantTried, setGrantTried] = useState(false);
  const [tariffs, setTariffs] = useState([]);
  const [cohortNames, setCohortNames] = useState({});
  const pagination = usePagination({ limit: 20 });
  const { page: paginationPage, limit: paginationLimit, applyMeta, reset: paginationReset } = pagination;
  // On a phone the status chips scroll sideways: the fade at the edge says there are more.
  const chips = useScrollFade();

  // Only the latest request may touch the roster: a slower earlier answer (an older search, the
  // page before a filter reset it) must not overwrite it.
  const requestId = useRef(0);

  const fetchEnrollments = useCallback(async () => {
    const id = ++requestId.current;
    try {
      setLoading(true);
      // Search and status are applied by the backend, so the paging and the count are the
      // filtered roster's, whatever its size.
      const result = await getCourseEnrollments(courseId, {
        page: paginationPage,
        limit: paginationLimit,
        search: query || undefined,
        status: status || undefined,
      });
      if (id !== requestId.current) return;
      setEnrollments(unwrapList(result));
      applyMeta(unwrapMeta(result, { limit: paginationLimit }));
      setError(null);
      setDenied(false);
    } catch (err) {
      if (id !== requestId.current) return;
      setDenied(err.code === 403);
      setError(err.code === 403
        ? t('courseCommerce.enrollments.accessDenied')
        : t('courseCommerce.enrollments.errorFailedToLoad'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [courseId, query, status, paginationPage, paginationLimit, applyMeta, t]);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    paginationReset();
  }, [query, status, paginationReset]);

  useEffect(() => {
    fetchEnrollments();
  }, [fetchEnrollments]);

  // Lazily resolve cohort names for any enrollment rows that carry a
  // cohortId + orgId (B2B licensed seats). Skip entirely if the current
  // enrollment list doesn't expose these fields, or if the cohorts API
  // isn't available yet — this is a nice-to-have, never a hard requirement.
  useEffect(() => {
    let cancelled = false;
    const needsCohorts = enrollments.some((e) => e?.cohortId);
    const orgIds = Array.from(new Set(enrollments.map((e) => e?.orgId).filter(Boolean)));
    if (!needsCohorts || orgIds.length === 0) return undefined;

    (async () => {
      const map = {};
      await Promise.all(orgIds.map(async (orgId) => {
        try {
          const res = await getCohorts(orgId);
          unwrapList(res).forEach((c) => { if (c?.id) map[c.id] = c.name; });
        } catch {
          // Cohorts endpoint unavailable — cohort names simply won't resolve.
        }
      }));
      if (!cancelled) setCohortNames((prev) => ({ ...prev, ...map }));
    })();

    return () => { cancelled = true; };
  }, [enrollments]);

  // Tariffs for the grant form. Only admins/moderators may read them; for anyone else the
  // select simply stays hidden.
  useEffect(() => {
    if (!showAddForm || !courseId) return undefined;
    let cancelled = false;
    getCourseTariffs(courseId)
      .then((list) => { if (!cancelled) setTariffs(list.filter((tier) => tier.isActive)); })
      .catch(() => { if (!cancelled) setTariffs([]); });
    return () => { cancelled = true; };
  }, [showAddForm, courseId]);

  const closeGrant = () => {
    if (saving) return;
    setShowAddForm(false);
    setGrantError(null);
    setGrantTried(false);
  };

  // Add a person by phone + name. The account is created when the phone has none, so the course
  // is already there when they first sign in with that number.
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setGrantError(null);
    if (!addForm.phone.trim() || !addForm.firstName.trim()) {
      setGrantTried(true);
      setGrantError(t('courseCommerce.enrollments.errorPhoneAndName'));
      return;
    }
    setSaving(true);

    try {
      const result = await grantCourseAccess({
        courseId,
        phone: addForm.phone.trim(),
        firstName: addForm.firstName.trim(),
        lastName: addForm.lastName.trim() || undefined,
        pricingTierId: addForm.pricingTierId || undefined,
        note: addForm.note.trim() || undefined,
      });
      const who = [addForm.firstName, addForm.lastName].map((v) => v.trim()).filter(Boolean).join(' ');
      const message = result?.alreadyEnrolled
        ? t('courseCommerce.enrollments.grantAlreadyEnrolled', { name: who })
        : result?.accountCreated
          ? t('courseCommerce.enrollments.grantCreated', { name: who, phone: formatPhone(addForm.phone.trim()) })
          : t('courseCommerce.enrollments.grantDone', { name: who });
      toast.success(message);
      setAddForm(EMPTY_GRANT);
      setShowAddForm(false);
      setGrantTried(false);
      paginationReset();
      await fetchEnrollments();
    } catch (err) {
      setGrantError(err?.code === 403
        ? t('courseCommerce.enrollments.accessDenied')
        : t('courseCommerce.enrollments.errorFailedToEnroll'));
    } finally {
      setSaving(false);
    }
  };

  const setField = (field) => (e) => setAddForm((prev) => ({ ...prev, [field]: e.target.value }));

  const getStudentName = (enrollment) =>
    enrollment.studentName || enrollment.user?.name || enrollment.user?.fullName || enrollment.student?.name || '—';

  // Phone and email: a student who signed up with a phone number has no email, and the roster is
  // where staff go to call them, so the phone comes first and survives a long address's ellipsis.
  const getStudentContact = (enrollment) => {
    const email = enrollment.studentEmail || enrollment.user?.email || enrollment.student?.email;
    const phone = enrollment.user?.phone ? formatPhone(enrollment.user.phone) : null;
    return [phone, email].filter(Boolean).join(' · ') || '—';
  };

  const getEnrollmentDate = (enrollment) =>
    enrollment.enrolledDate || enrollment.enrolledAt || enrollment.createdAt;

  const getProgress = (enrollment) =>
    Math.max(0, Math.min(100, Math.round(enrollment.progress ?? enrollment.progressPercent ?? 0)));

  // The words of the three statuses the roster knows; '' is the "all" chip. A status the page
  // has no word for gives undefined, so StatusBadge names it from the shared status.* words
  // instead of printing the raw value.
  const statusLabel = (value) => {
    switch (String(value || '').toLowerCase()) {
      case 'active': return t('courseCommerce.enrollments.statusActive');
      case 'completed': return t('courseCommerce.enrollments.statusCompleted');
      case 'cancelled': return t('courseCommerce.enrollments.statusCancelled');
      case '': return t('courseCommerce.enrollments.allStatuses');
      default: return undefined;
    }
  };

  const clearFilters = () => {
    setSearch('');
    setQuery('');
    setStatus('');
  };

  // Extra columns only appear once the API actually returns the fields they
  // need — older/plain enrollment payloads render exactly as before.
  const showBadgesColumn = enrollments.some((e) => e?.source || e?.orgId || e?.org || e?.expiresAt);
  const showCohortColumn = enrollments.some((e) => e?.cohortId);
  const hasFilters = Boolean(query || status);

  const grantButton = (
    <button
      type="button"
      onClick={() => setShowAddForm(true)}
      className="ds-btn ds-btn-primary enr-cta"
      aria-haspopup="dialog"
    >
      <UserPlus aria-hidden="true" />
      {t('courseCommerce.enrollments.addStudentCta')}
    </button>
  );

  let results;
  if (loading && enrollments.length === 0 && !error) {
    results = <LoadingState skeleton="table" rows={6} message={t('courseCommerce.enrollments.loading')} />;
  } else if (enrollments.length === 0) {
    if (error) {
      results = (
        <PageEmptyState
          variant={denied ? 'forbidden' : 'error'}
          description={error}
          onRetry={denied ? undefined : fetchEnrollments}
        />
      );
    } else if (hasFilters) {
      results = (
        <PageEmptyState
          variant="search"
          title={t('courseCommerce.enrollments.filteredEmptyTitle')}
          description={t('courseCommerce.enrollments.filteredEmptyDescription')}
          // The page's own yellow "add student" button is on screen: clearing is the quieter one
          secondaryLabel={t('courseCommerce.common.clearFilters')}
          onSecondaryAction={clearFilters}
        />
      );
    } else {
      // The page's own yellow button is already on screen: no second one here.
      results = (
        <PageEmptyState
          variant="students"
          title={t('courseCommerce.enrollments.emptyTitle')}
          description={t('courseCommerce.enrollments.emptyDescription')}
        />
      );
    }
  } else {
    results = (
      <TableCard
        className="enr-table-card"
        tall={enrollments.length > TALL_ROWS}
        aria-label={t('courseCommerce.enrollments.title')}
        footer={(
          <div className="enr-foot">
            <span className="enr-count">{t('courseCommerce.enrollments.countEnrolled', { count: pagination.total })}</span>
            <Pagination state={pagination} hideOnSinglePage />
          </div>
        )}
      >
        <table className="ui-table ui-table--stack enr-table">
          <thead>
            <tr>
              <th>{t('courseCommerce.enrollments.tableStudent')}</th>
              <th>{t('courseCommerce.enrollments.tableStatus')}</th>
              {showBadgesColumn && <th>{t('activities.enrollmentBadges.columnHeader')}</th>}
              {showCohortColumn && <th>{t('activities.enrollmentBadges.cohortColumn')}</th>}
              <th>{t('courseCommerce.enrollments.tableEnrolled')}</th>
              <th className="enr-th-progress">{t('courseCommerce.enrollments.tableProgress')}</th>
            </tr>
          </thead>
          <tbody>
            {enrollments.map((enrollment) => {
              const name = getStudentName(enrollment);
              const contact = getStudentContact(enrollment);
              const prog = getProgress(enrollment);
              const date = getEnrollmentDate(enrollment);
              const state = getEnrollmentStatus(enrollment);
              const userId = enrollment.user?.id || enrollment.userId;
              return (
                <tr key={enrollment.id}>
                  <td>
                    <div className="ui-cell-person">
                      <Avatar name={name} id={userId || name} />
                      <span className="ui-cell-person__text">
                        {userId ? (
                          <Link
                            className="enr-name-link"
                            to={`/students/${userId}?courseId=${courseId}`}
                          >
                            <span className="ui-cell-person__name">{name}</span>
                          </Link>
                        ) : (
                          <span className="ui-cell-person__name">{name}</span>
                        )}
                        <span className="ui-cell-person__sub" title={contact}>{contact}</span>
                      </span>
                    </div>
                  </td>
                  <td data-label={t('courseCommerce.enrollments.tableStatus')}>
                    <StatusBadge status={state}>{statusLabel(state)}</StatusBadge>
                  </td>
                  {showBadgesColumn && (
                    <td data-label={t('activities.enrollmentBadges.columnHeader')}>
                      <EnrollmentLicense enrollment={enrollment} t={t} />
                    </td>
                  )}
                  {showCohortColumn && (
                    <td className="enr-cohort" data-label={t('activities.enrollmentBadges.cohortColumn')}>
                      {enrollment?.cohortId ? (cohortNames[enrollment.cohortId] || '—') : '—'}
                    </td>
                  )}
                  <td className="enr-date" data-label={t('courseCommerce.enrollments.tableEnrolled')}>
                    {date ? formatDate(date) : '—'}
                  </td>
                  <td data-label={t('courseCommerce.enrollments.tableProgress')}>
                    <div className={`enr-progress${prog >= 100 ? ' is-complete' : ''}`}>
                      <ProgressBar className="enr-bar" value={prog} label={t('courseCommerce.enrollments.tableProgress')} />
                      <span className="enr-pct">{formatPercent(prog)}</span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableCard>
    );
  }

  return (
    <div className="ui-stack enr-page">
      {!embedded && (
        <PageHead
          back={{ to: `/courses/edit/${courseId}?tab=students`, label: t('courseCommerce.common.backToCourse') }}
          eyebrow={<span className="enr-eyebrow">{courseTitle || t('courseCommerce.common.parent')}</span>}
          title={t('courseCommerce.enrollments.title')}
          description={t('courseCommerce.enrollments.subtitle')}
          actions={grantButton}
        />
      )}

      <div className="enr-toolbar">
        <SearchInput
          className="enr-search"
          value={search}
          onChange={setSearch}
          placeholder={t('courseCommerce.enrollments.searchPlaceholder')}
        />
        <div
          ref={chips.ref}
          className={`ui-chip-row enr-filters ${chips.fade}`.trim()}
          role="group"
          aria-label={t('courseCommerce.enrollments.statusFilterAriaLabel')}
        >
          {STATUS_FILTERS.map((value) => (
            <button
              key={value || 'all'}
              type="button"
              className="ui-chip"
              aria-pressed={status === value}
              onClick={() => setStatus(value)}
            >
              {statusLabel(value)}
            </button>
          ))}
        </div>
        {embedded && <div className="enr-toolbar-end">{grantButton}</div>}
      </div>

      {error && enrollments.length > 0 && (
        <div className="ui-alert" role="alert">
          <CircleAlert aria-hidden="true" />
          <p>{error}</p>
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={fetchEnrollments}>
            {t('ui.retry')}
          </button>
        </div>
      )}

      <div className="enr-results" aria-busy={loading}>
        {results}
      </div>

      <Modal
        open={showAddForm}
        onClose={closeGrant}
        title={t('courseCommerce.enrollments.addStudentFormTitle')}
        size="lg"
        as="form"
        onSubmit={handleAddSubmit}
        formProps={{ noValidate: true }}
        footer={(
          <>
            <button type="button" onClick={closeGrant} disabled={saving} className="ds-btn ds-btn-secondary">
              {t('courseCommerce.enrollments.cancelButton')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`ds-btn ds-btn-primary${saving ? ' is-loading' : ''}`}
            >
              <span>{saving ? t('courseCommerce.enrollments.addingButton') : t('courseCommerce.enrollments.addStudentSubmit')}</span>
            </button>
          </>
        )}
      >
        <div className="enr-grant">
          <p className="ui-hint enr-grant-hint">{t('courseCommerce.enrollments.grantHint')}</p>
          <div className="ui-form-grid">
            <div className="ui-field">
              <label className="ui-label" htmlFor="grant-phone">
                {t('courseCommerce.enrollments.phoneLabel')}<span className="ui-req" aria-hidden="true">*</span>
              </label>
              <input
                id="grant-phone"
                type="tel"
                inputMode="tel"
                required
                autoComplete="off"
                value={addForm.phone}
                onChange={setField('phone')}
                placeholder={t('courseCommerce.enrollments.phonePlaceholder')}
                aria-invalid={grantTried && !addForm.phone.trim() ? 'true' : undefined}
              />
            </div>
            <div className="ui-field">
              <label className="ui-label" htmlFor="grant-first">
                {t('courseCommerce.enrollments.firstNameLabel')}<span className="ui-req" aria-hidden="true">*</span>
              </label>
              <input
                id="grant-first"
                type="text"
                required
                autoComplete="off"
                value={addForm.firstName}
                onChange={setField('firstName')}
                aria-invalid={grantTried && !addForm.firstName.trim() ? 'true' : undefined}
              />
            </div>
            <div className="ui-field">
              <label className="ui-label" htmlFor="grant-last">{t('courseCommerce.enrollments.lastNameLabel')}</label>
              <input
                id="grant-last"
                type="text"
                autoComplete="off"
                value={addForm.lastName}
                onChange={setField('lastName')}
              />
            </div>
            {tariffs.length > 0 && (
              <div className="ui-field">
                <label className="ui-label" htmlFor="grant-tariff">{t('courseCommerce.enrollments.tariffLabel')}</label>
                <Dropdown
                  id="grant-tariff"
                  value={addForm.pricingTierId}
                  onChange={(value) => setAddForm((prev) => ({ ...prev, pricingTierId: value }))}
                  options={[
                    { value: '', label: t('courseCommerce.enrollments.tariffNone') },
                    ...tariffs.map((tier) => ({
                      value: tier.id,
                      label: `${tier.name} — ${formatMoney(tier.price, tier.currency || 'UZS')}`,
                    })),
                  ]}
                  ariaLabel={t('courseCommerce.enrollments.tariffLabel')}
                />
              </div>
            )}
            <div className="ui-field is-wide">
              <label className="ui-label" htmlFor="grant-note">{t('courseCommerce.enrollments.noteLabel')}</label>
              <input
                id="grant-note"
                type="text"
                maxLength={500}
                autoComplete="off"
                value={addForm.note}
                onChange={setField('note')}
                placeholder={t('courseCommerce.enrollments.notePlaceholder')}
              />
            </div>
          </div>
          {grantError && (
            <div className="ui-alert enr-grant-error" role="alert">
              <CircleAlert aria-hidden="true" />
              <p>{grantError}</p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default CourseEnrollments;
